import React, { useEffect, useState } from "react";
import { searchDiscogsByBarcode, getDiscogsRelease, aiEnrichProduct } from "../../services/discogs";
import { listAll, createDoc } from "../../services/firestore";
import { uploadImageToR2 } from "../../services/r2";
import BarcodeCameraScanner from "../../components/BarcodeCameraScanner";
import SpotifyAlbumPicker from "../../components/SpotifyAlbumPicker";
import { Link } from "react-router-dom";
import { Search, Loader2, ImageIcon, Check, ExternalLink, RotateCcw, Music2, Package, Sparkles, Languages, Camera, AlertTriangle } from "lucide-react";

const STEPS = { IDLE: 0, RESULTS: 1, DETAIL: 2, EDIT: 3, DONE: 4 };

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function localizedName(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.az || v.en || v.ru || Object.values(v).find((x) => typeof x === "string") || "";
}

function parseSpotifyId(url, kind) {
  if (!url) return "";
  const s = String(url).trim();
  // Just the 22-char ID
  if (/^[a-zA-Z0-9]{22}$/.test(s)) return s;
  // spotify:kind:{id}
  const m0 = s.match(new RegExp(`spotify:${kind}:([a-zA-Z0-9]{22})`));
  if (m0) return m0[1];
  // Any form with {kind}/{id} — full URL, embed URL, or bare path like "/album/{id}"
  const m1 = s.match(new RegExp(`(?:^|[/:])${kind}[/:]([a-zA-Z0-9]{22})(?:[?#]|$)`));
  if (m1) return m1[1];
  return "";
}

// Normalise a Spotify URL for storage — always canonical https://open.spotify.com/album/{id}
function normalizeSpotifyUrl(url, kind = "album") {
  const id = parseSpotifyId(url, kind);
  return id ? `https://open.spotify.com/${kind}/${id}` : "";
}

export default function AdminVaultBarcode() {
  const [barcode, setBarcode] = useState("");
  const [step, setStep] = useState(STEPS.IDLE);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pickedImageUris, setPickedImageUris] = useState([]); // multi-select
  const [form, setForm] = useState(null);
  const [cats, setCats] = useState([]);
  const [artists, setArtists] = useState([]);
  const [savedId, setSavedId] = useState("");
  const [descTab, setDescTab] = useState("az");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [spotifyPickerOpen, setSpotifyPickerOpen] = useState(false);
  const [spotifyStatus, setSpotifyStatus] = useState("idle"); // idle | verified | unverified | resolving
  const [spotifyResolveErr, setSpotifyResolveErr] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [duplicateProduct, setDuplicateProduct] = useState(null); // {id, title, slug, ...}

  useEffect(() => {
    listAll("categories").then(setCats).catch(() => {});
    listAll("artists").then(setArtists).catch(() => {});
  }, []);

  const reset = () => {
    setStep(STEPS.IDLE);
    setResults([]);
    setDetail(null);
    setPickedImageUris([]);
    setForm(null);
    setError("");
    setBarcode("");
    setSavedId("");
    setDescTab("az");
    setDuplicateProduct(null);
  };

  // Check if a product with the same barcode already exists in Firestore
  const checkDuplicateByBarcode = async (bc) => {
    if (!bc) return null;
    try {
      // Bypass local cache — duplicate check must always hit fresh data
      const all = await listAll("products", { noCache: true });
      const norm = String(bc).trim().toLowerCase();
      return all.find((p) => String(p.barcode || "").trim().toLowerCase() === norm) || null;
    } catch (_) {
      return null;
    }
  };

  const doSearch = async (e) => {
    e?.preventDefault?.();
    if (!barcode.trim()) return;
    setBusy(true);
    setError("");
    setDuplicateProduct(null);
    try {
      // ✅ Duplicate check BEFORE hitting Discogs — save time & block accidents.
      const dup = await checkDuplicateByBarcode(barcode.trim());
      if (dup) {
        setDuplicateProduct(dup);
        setBusy(false);
        return; // Do not proceed with Discogs search
      }

      const data = await searchDiscogsByBarcode(barcode.trim());
      setResults(data.results || []);
      setStep(STEPS.RESULTS);
      if (!data.results?.length) setError("Bu barkod üzrə heç bir nəticə tapılmadı.");
    } catch (ex) {
      setError(ex.message || "Axtarış alınmadı.");
    } finally {
      setBusy(false);
    }
  };

  const pickRelease = async (rel) => {
    setBusy(true);
    setError("");
    try {
      const d = await getDiscogsRelease(rel.id);
      setDetail(d);
      // Auto-select ALL Discogs images by default (user can deselect ones they don't want)
      const allUris = (d.images || []).map((im) => im.uri).filter(Boolean);
      // Ensure the primary image is first
      const primaryImg = (d.images || []).find((im) => im.type === "primary") || (d.images || [])[0];
      let orderedUris = allUris;
      if (primaryImg?.uri) {
        orderedUris = [primaryImg.uri, ...allUris.filter((u) => u !== primaryImg.uri)];
      }
      setPickedImageUris(orderedUris);
      setStep(STEPS.DETAIL);
    } catch (ex) {
      setError(ex.message || "Detal alınmadı.");
    } finally {
      setBusy(false);
    }
  };

  const toggleImage = (uri) => {
    setPickedImageUris((prev) => {
      if (prev.includes(uri)) return prev.filter((u) => u !== uri);
      return [...prev, uri];
    });
  };

  const setPrimaryImage = (uri) => {
    setPickedImageUris((prev) => {
      const list = prev.includes(uri) ? prev : [uri, ...prev];
      return [uri, ...list.filter((u) => u !== uri)];
    });
  };

  const proceedToEdit = async () => {
    if (!detail) return;
    // Auto-match categories from Discogs genres + styles → existing categories
    // Uses WORD-BOUNDARY matching (not substring) so we don't over-match
    // (e.g. "rock" shouldn't match "rockabilly-derivative" etc.).
    // Also caps at the top 3 matches so we don't select 10 categories at once.
    const genresStyles = [
      ...(detail.genres || []),
      ...(detail.styles || []),
    ]
      .map((t) => String(t || "").toLowerCase().trim())
      .filter(Boolean);

    const norm = (s) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9əöüğıçş\s]/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

    // Tokenize category candidate strings into words for word-boundary matching.
    const catScore = (c) => {
      const candStrs = [c.name, c.slug, c.name_en, c.name_ru, c.name_az]
        .filter(Boolean)
        .map(norm);
      let score = 0;
      for (const term of genresStyles) {
        const t = norm(term);
        if (!t) continue;
        for (const cand of candStrs) {
          if (!cand) continue;
          // EXACT match (highest priority)
          if (cand === t) { score += 10; continue; }
          // Whole-word match — term appears as a full word inside candidate
          const wordRe = new RegExp(`(^|\\s)${t.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}(\\s|$)`);
          if (wordRe.test(cand)) score += 5;
          // Or candidate is a whole word inside term
          const wordReRev = new RegExp(`(^|\\s)${cand.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}(\\s|$)`);
          if (wordReRev.test(t)) score += 3;
        }
      }
      return score;
    };

    const scored = cats
      .map((c) => ({ c, s: catScore(c) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 3); // cap at 3 categories max

    // De-duplicate ids just in case.
    const matchedCategoryIds = Array.from(new Set(scored.map((x) => x.c.id)));

    // Base form (before AI)
    const primaryUri = pickedImageUris[0] || "";
    const base = {
      title: detail.title || "",
      artist_name: detail.artist || "",
      artist_id: "",
      category_ids: matchedCategoryIds,
      price_azn: 0,
      cover_url: primaryUri,
      image_uris: [...pickedImageUris], // remote uris; will be uploaded to R2 on save
      description_az: "",
      description_en: "",
      description_ru: "",
      catalog_no: (detail.labels && detail.labels[0]?.catno) || "",
      format: detail.format_display || "",
      rpm: "33 ⅓ RPM",
      weight: "180g",
      release_date: detail.year ? String(detail.year) : "",
      spotify_album_url: "",
      barcode: barcode.trim(),
      stock: 1,
      featured: false,
      new_arrival: true,
      is_used: false,
    };
    setForm(base);
    setStep(STEPS.EDIT);

    // Fire AI enrichment (non-blocking — user can start editing while it runs)
    setAiBusy(true);
    try {
      const enriched = await aiEnrichProduct({
        artist: detail.artist,
        album: detail.title,
        year: detail.year || null,
        genres: detail.genres || [],
        styles: detail.styles || [],
        discogs_notes: detail.notes || "",
        format_display: detail.format_display || "",
        barcode: barcode.trim(),
      });
      const aiUrl = enriched.spotify_album_url || "";
      const aiVerified = !!enriched.spotify_verified;

      setForm((f) => ({
        ...f,
        // Only auto-fill if AI verified a REAL album URL. If AI returned a
        // fallback search URL, leave the field empty so admin knows to paste
        // the shared album link manually.
        spotify_album_url: aiVerified ? aiUrl : "",
        description_az: enriched.description_az || f.description_az,
        description_en: enriched.description_en || f.description_en,
        description_ru: enriched.description_ru || f.description_ru,
      }));
      setSpotifyStatus(aiVerified ? "verified" : "unverified");

      // If AI's Spotify guess wasn't verified, ALSO try live Spotify search
      // via client credentials (if set). This overwrites with a REAL match.
      if (!aiVerified) {
        try {
          const { spotifySearchReal } = await import("../../services/discogs");
          const result = await spotifySearchReal({
            artist: detail.artist,
            album: detail.title,
            barcode: barcode.trim(),
            limit: 1,
          });
          if (result.albums && result.albums.length > 0) {
            const best = result.albums[0];
            setForm((f) => ({ ...f, spotify_album_url: best.url }));
            setSpotifyStatus("verified");
          }
        } catch (spErr) {
          // Silent — Spotify creds not set or search failed. User can pick manually.
          console.info("spotify live search skipped:", spErr.message);
        }
      }
    } catch (ex) {
      console.warn("AI enrich failed", ex);
      setError(`AI ilə zənginləşdirmə uğursuz oldu: ${ex.message || ex}. Manual doldurmaq olar.`);
    } finally {
      setAiBusy(false);
    }
  };

  const rerunAI = async () => {
    if (!detail) return;
    setAiBusy(true);
    setError("");
    try {
      const enriched = await aiEnrichProduct({
        artist: form.artist_name || detail.artist,
        album: form.title || detail.title,
        year: detail.year || null,
        genres: detail.genres || [],
        styles: detail.styles || [],
        discogs_notes: detail.notes || "",
        format_display: detail.format_display || "",
        barcode: form.barcode || barcode.trim(),
      });
      setForm((f) => ({
        ...f,
        spotify_album_url: enriched.spotify_verified ? (enriched.spotify_album_url || f.spotify_album_url) : f.spotify_album_url,
        description_az: enriched.description_az || f.description_az,
        description_en: enriched.description_en || f.description_en,
        description_ru: enriched.description_ru || f.description_ru,
      }));
      if (enriched.spotify_verified) setSpotifyStatus("verified");
      else if (!form.spotify_album_url) setSpotifyStatus("unverified");
    } catch (ex) {
      setError(`AI: ${ex.message || ex}`);
    } finally {
      setAiBusy(false);
    }
  };

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleCat = (id) => setForm((s) => ({
    ...s,
    category_ids: s.category_ids.includes(id) ? s.category_ids.filter((x) => x !== id) : [...s.category_ids, id],
  }));

  const save = async () => {
    if (!form.title) return alert("Başlıq lazımdır.");
    setBusy(true);
    setError("");
    try {
      // ✅ Final duplicate check (in case another admin added it while this wizard was open)
      if (form.barcode) {
        const dup = await checkDuplicateByBarcode(form.barcode);
        if (dup) {
          setDuplicateProduct(dup);
          setError("Bu barkod artıq başqa məhsulda istifadə olunur — yadda saxlanmadı.");
          setBusy(false);
          return;
        }
      }

      // Determine which images to upload: prefer form.image_uris (multi-select), fall back to cover_url
      const remoteUris = (form.image_uris && form.image_uris.length ? form.image_uris : [form.cover_url]).filter(Boolean);
      const uploadedUrls = [];
      setUploadProgress({ current: 0, total: remoteUris.length });
      for (let i = 0; i < remoteUris.length; i++) {
        const src = remoteUris[i];
        try {
          if (src && src.startsWith("http") && !src.includes(".r2.") && !src.includes("cloudflarestorage")) {
            const resp = await fetch(src);
            const blob = await resp.blob();
            const file = new File([blob], `discogs-${Date.now()}-${i}.jpg`, { type: blob.type || "image/jpeg" });
            const url = await uploadImageToR2(file, { folder: "products" });
            uploadedUrls.push(url);
          } else {
            uploadedUrls.push(src);
          }
        } catch (e) {
          console.warn(`image ${i} upload failed, using original url`, e);
          uploadedUrls.push(src);
        }
        setUploadProgress({ current: i + 1, total: remoteUris.length });
      }
      const cover_url = uploadedUrls[0] || form.cover_url || "";
      const images = uploadedUrls;

      // ✅ Ensure artist exists — REFETCH artists from Firestore first so we don't
      // duplicate an artist that was created moments ago in another wizard run.
      let artist_id = form.artist_id;
      if (!artist_id && form.artist_name) {
        const target = String(form.artist_name).trim().toLowerCase();
        // Refresh live artists list (bypass stale local state)
        let freshArtists = artists;
        try {
          freshArtists = await listAll("artists", { noCache: true });
        } catch (_) { /* fallback to local state */ }
        const normName = (v) => String(localizedName(v) || "").trim().toLowerCase();
        const existing = freshArtists.find((a) => normName(a.name) === target);
        if (existing) {
          artist_id = existing.id;
        } else {
          // Also try match by slug (in case a user typed a name with different spacing)
          const slug = slugify(form.artist_name);
          const bySlug = freshArtists.find((a) => (a.slug || "").toLowerCase() === slug);
          if (bySlug) {
            artist_id = bySlug.id;
          } else {
            const created = await createDoc("artists", {
              name: form.artist_name,
              slug,
              bio: "",
              image_url: "",
              featured: false,
            });
            artist_id = created.id;
            // Keep local state fresh so subsequent runs in same session also see it
            setArtists([...(freshArtists || []), { id: created.id, name: form.artist_name, slug }]);
          }
        }
      }

      // Extract clean Spotify album ID (only if URL is a real album URL, not a search URL)
      const spotifyAlbumId = parseSpotifyId(form.spotify_album_url, "album");
      const cleanSpotifyUrl = normalizeSpotifyUrl(form.spotify_album_url, "album");

      const data = {
        title: form.title,
        title_en: form.title,
        title_ru: form.title,
        slug: slugify(form.title + "-" + (form.artist_name || "") + "-" + (form.catalog_no || Date.now())),
        artist_id,
        category_ids: form.category_ids,
        category_id: form.category_ids[0] || "",
        price_azn: parseFloat(form.price_azn) || 0,
        cover_url,
        images, // NEW: gallery of all uploaded images (first is cover)
        // Use AI-generated multilang descriptions
        description: form.description_az || "",
        description_az: form.description_az || "",
        description_en: form.description_en || "",
        description_ru: form.description_ru || "",
        catalog_no: form.catalog_no || "",
        format: form.format || "",
        rpm: form.rpm || "",
        weight: form.weight || "",
        release_date: form.release_date || "",
        // NO tracklist — user only wants Spotify album link
        tracklist: [],
        spotify_track_id: "",
        // Only save spotify_album_id if we successfully parsed a valid 22-char ID.
        // Never store a search-url or malformed data in the id field — otherwise the
        // embed on the product page becomes broken.
        spotify_album_id: spotifyAlbumId || "",
        spotify_album_url: cleanSpotifyUrl || form.spotify_album_url || "",
        barcode: form.barcode || "",
        stock: parseInt(form.stock, 10) || 0,
        featured: !!form.featured,
        new_arrival: !!form.new_arrival,
        new_arrival_set_at: form.new_arrival ? new Date().toISOString() : null,
        is_used: !!form.is_used,
        limited: false,
      };
      const created = await createDoc("products", data);
      setSavedId(created.id);
      setStep(STEPS.DONE);
    } catch (ex) {
      setError(ex.message || "Yadda saxlanmadı.");
    } finally {
      setBusy(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-6" data-testid="vault-barcode">
      {/* Step 0: Barcode input */}
      <form onSubmit={doSearch} className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 border border-[var(--border)] px-4 py-3 flex-1 bg-[var(--bg)]">
          <Search size={16} className="text-[var(--text-muted)] flex-shrink-0" />
          <input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Barkod nömrəsi (məs: 8698540824082)"
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="vault-barcode-input"
            inputMode="numeric"
          />
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            className="text-[var(--accent)] hover:text-[var(--primary)] flex items-center gap-1 text-xs whitespace-nowrap"
            title="Kamera ilə skan"
            data-testid="vault-camera-btn"
          >
            <Camera size={14} /> Kamera
          </button>
        </div>
        <button type="submit" disabled={busy || !barcode.trim()} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="vault-search-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Axtar
        </button>
        {step !== STEPS.IDLE && (
          <button type="button" onClick={reset} className="px-4 py-3 border border-[var(--border)] text-sm hover:border-[var(--accent)]">
            <RotateCcw size={14} className="inline mr-1" /> Sıfırla
          </button>
        )}
      </form>

      {/* Barcode camera scanner modal */}
      <BarcodeCameraScanner
        open={cameraOpen}
        onClose={(code) => {
          setCameraOpen(false);
          if (code) {
            setBarcode(code);
            // Auto-search
            setTimeout(() => {
              const btn = document.querySelector('[data-testid="vault-search-btn"]');
              if (btn) btn.click();
            }, 200);
          }
        }}
      />

      {error && (
        <div className="border border-[var(--accent)]/40 bg-[var(--accent)]/5 text-[var(--accent)] p-4 text-sm" data-testid="vault-error">
          {error}
        </div>
      )}

      {/* Duplicate product warning */}
      {duplicateProduct && (
        <div
          className="border-2 border-amber-500/60 bg-amber-500/10 p-4 sm:p-5 flex flex-col sm:flex-row gap-4"
          data-testid="vault-duplicate-notice"
        >
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center">
              <AlertTriangle size={20} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-base text-amber-600 dark:text-amber-400 mb-1">
              Bu məhsul artıq sistemdə mövcuddur
            </div>
            <div className="text-sm text-[var(--text-2)] mb-3">
              Barkod <b className="font-mono">{barcode}</b> ilə eyni məhsul artıq əlavə edilib —
              yenidən yaratmaq olmaz.
            </div>
            <div className="flex items-center gap-3 border border-[var(--border)] bg-[var(--bg)] p-3">
              {duplicateProduct.cover_url ? (
                <img
                  src={duplicateProduct.cover_url}
                  alt=""
                  className="w-14 h-14 object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-14 h-14 flex items-center justify-center bg-[var(--surface)] flex-shrink-0">
                  <ImageIcon size={18} className="text-[var(--text-muted)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider truncate">
                  {duplicateProduct.artist_id ? (artists.find((a) => a.id === duplicateProduct.artist_id)?.name || "") : ""}
                </div>
                <div className="font-medium text-[var(--text)] truncate">{duplicateProduct.title || "—"}</div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  {duplicateProduct.price_azn ? `${duplicateProduct.price_azn} AZN` : ""}
                  {duplicateProduct.stock !== undefined ? ` · Stok: ${duplicateProduct.stock}` : ""}
                </div>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                {duplicateProduct.slug && (
                  <Link
                    to={`/product/${duplicateProduct.slug}`}
                    target="_blank"
                    className="text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--text-2)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                    data-testid="vault-duplicate-view"
                  >
                    <ExternalLink size={11} /> Bax
                  </Link>
                )}
                <Link
                  to="/admin/products"
                  className="text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--text-2)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                  data-testid="vault-duplicate-edit"
                >
                  Redaktə
                </Link>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setDuplicateProduct(null); setBarcode(""); }}
              className="mt-3 text-xs text-[var(--text-muted)] hover:text-[var(--text)] underline"
            >
              Yeni barkod skan et
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Results list */}
      {step === STEPS.RESULTS && results.length > 0 && (
        <div>
          <div className="text-sm text-[var(--text-muted)] mb-3">{results.length} nəticə tapıldı — birini seçin</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => (
              <button
                key={r.id}
                onClick={() => pickRelease(r)}
                disabled={busy}
                className="text-left border border-[var(--border)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors p-3 flex gap-3"
                data-testid={`vault-result-${r.id}`}
              >
                {r.thumb ? (
                  <img src={r.thumb} alt="" className="w-20 h-20 object-cover flex-shrink-0" />
                ) : (
                  <div className="w-20 h-20 bg-[var(--bg)] flex items-center justify-center flex-shrink-0"><ImageIcon size={20} /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.artist || "—"}</div>
                  <div className="text-sm text-[var(--text-2)] truncate">{r.album}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-1">
                    {r.year || ""} {r.country ? `· ${r.country}` : ""}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">{(r.format || []).join(", ")}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Detail + image picker */}
      {step === STEPS.DETAIL && detail && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-[11px] tracking-[0.14em] text-[var(--text-muted)]">{detail.artist}</div>
              <div className="font-display text-2xl">{detail.title}</div>
              <div className="text-sm text-[var(--text-2)] mt-1">
                {detail.year} · {detail.format_display} · {(detail.genres || []).concat(detail.styles || []).slice(0, 3).join(", ")}
              </div>
            </div>
            <a href={detail.uri} target="_blank" rel="noreferrer" className="text-sm text-[var(--accent)] inline-flex items-center gap-1">
              Discogs <ExternalLink size={12} />
            </a>
          </div>

          <div>
            <div className="text-sm font-medium mb-2 flex items-center justify-between flex-wrap gap-2">
              <span>Örtük şəkillərini seçin ({(detail.images || []).length} tapıldı, {pickedImageUris.length} seçildi)</span>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setPickedImageUris((detail.images || []).map((im) => im.uri).filter(Boolean))}
                  className="px-2 py-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--text-2)]"
                >Hamısını seç</button>
                <button
                  type="button"
                  onClick={() => setPickedImageUris([])}
                  className="px-2 py-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--text-2)]"
                >Təmizlə</button>
              </div>
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mb-3">
              İlk seçilən şəkil əsas örtük (cover) olacaq. Sıra dəyişmək üçün şəklə basaraq <b>&quot;Əsas et&quot;</b> istifadə edin.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {(detail.images || []).map((im, i) => {
                const isSelected = pickedImageUris.includes(im.uri);
                const orderIdx = pickedImageUris.indexOf(im.uri);
                const isPrimary = orderIdx === 0;
                return (
                  <div
                    key={i}
                    className={`relative aspect-square border-2 transition-all cursor-pointer ${
                      isSelected ? (isPrimary ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/40" : "border-[var(--primary)]") : "border-[var(--border)] hover:border-[var(--text-2)]"
                    }`}
                    data-testid={`vault-image-${i}`}
                    onClick={() => toggleImage(im.uri)}
                  >
                    <img src={im.uri} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    {isSelected && (
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full text-white flex items-center justify-center text-[11px] font-bold ${isPrimary ? "bg-[var(--accent)]" : "bg-[var(--primary)]"}`}>
                        {orderIdx + 1}
                      </div>
                    )}
                    {isSelected && !isPrimary && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPrimaryImage(im.uri); }}
                        className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 bg-black/70 text-white hover:bg-[var(--accent)]"
                      >Əsas et</button>
                    )}
                    {isSelected && isPrimary && (
                      <div className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 bg-[var(--accent)] text-white">Əsas</div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1 py-0.5 truncate">
                      {im.type} · {im.width}×{im.height}
                    </div>
                  </div>
                );
              })}
              {!detail.images?.length && <div className="text-sm text-[var(--text-muted)] col-span-full">Şəkil tapılmadı</div>}
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep(STEPS.RESULTS)} className="px-4 py-2 border border-[var(--border)] text-sm hover:border-[var(--text-2)]">Geri</button>
            <button onClick={proceedToEdit} disabled={!pickedImageUris.length} className="btn-primary flex-1 justify-center" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="vault-proceed-edit">
              <Sparkles size={14} /> Davam et — AI ilə zənginləşdir ({pickedImageUris.length} şəkil)
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Edit form with AI descriptions */}
      {step === STEPS.EDIT && form && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            {/* Primary cover */}
            <div className="aspect-square border border-[var(--border)] mb-2 relative">
              {form.cover_url && <img src={form.cover_url} alt="" className="w-full h-full object-cover" />}
              {(form.image_uris?.length || 0) > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded">
                  1 / {form.image_uris.length}
                </div>
              )}
            </div>
            {/* Additional images strip */}
            {(form.image_uris?.length || 0) > 1 && (
              <div className="grid grid-cols-4 gap-1 mb-2">
                {form.image_uris.map((uri, idx) => (
                  <div key={idx} className={`aspect-square border ${idx === 0 ? "border-[var(--accent)]" : "border-[var(--border)]"} relative`}>
                    <img src={uri} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-0.5 left-0.5 bg-black/70 text-white text-[9px] px-1 rounded">{idx + 1}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-[var(--text-muted)] mb-3">Yadda saxlandıqda bütün şəkillər R2-yə köçürüləcək</div>

            {/* PRICE — PRIMARY FIELD, HIGHLIGHTED */}
            <div className="border-2 border-[var(--accent)] bg-[var(--accent)]/5 p-3 mb-3">
              <label className="block">
                <span className="text-[11px] tracking-[0.14em] uppercase text-[var(--accent)] font-bold block mb-1">
                  ★ Qiymət (AZN) — Ən vacib
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={form.price_azn}
                  onChange={(e) => setField("price_azn", e.target.value)}
                  className="w-full bg-[var(--bg)] border-2 border-[var(--accent)]/40 focus:border-[var(--accent)] px-3 py-3 text-xl font-mono outline-none"
                  data-testid="vault-f-price"
                  placeholder="0.00"
                  autoFocus
                />
              </label>
              <div className="text-[10px] text-[var(--text-muted)] mt-1">Digər sahələr AI tərəfindən avtomatik dolduruldu — istəsəniz düzəldə bilərsiniz.</div>
            </div>

            <Field label="Stok">
              <input type="number" value={form.stock} onChange={(e) => setField("stock", e.target.value)} className="input" />
            </Field>

            <button
              onClick={rerunAI}
              disabled={aiBusy}
              className="w-full mt-3 px-3 py-2 border border-[var(--accent)]/40 bg-[var(--accent)]/5 text-[var(--accent)] text-sm hover:bg-[var(--accent)]/10 flex items-center justify-center gap-2"
              data-testid="vault-ai-rerun"
            >
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              AI ilə yenidən doldur
            </button>
          </div>

          <div className="md:col-span-2 space-y-3">
            <Field label="Başlıq" required>
              <input value={form.title} onChange={(e) => setField("title", e.target.value)} className="input" data-testid="vault-f-title" />
            </Field>
            <Field label="Sənətçi adı">
              <input value={form.artist_name} onChange={(e) => setField("artist_name", e.target.value)} className="input" data-testid="vault-f-artist" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Barkod">
                <input value={form.barcode} onChange={(e) => setField("barcode", e.target.value)} className="input" />
              </Field>
              <Field label="Katalog №">
                <input value={form.catalog_no} onChange={(e) => setField("catalog_no", e.target.value)} className="input" />
              </Field>
            </div>

            <Field label={`Kateqoriya (${form.category_ids?.length || 0} seçildi — AI avtomatik uyğunlaşdırdı)`}>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border border-[var(--border)] p-2 bg-[var(--bg)]">
                {cats.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCat(c.id)}
                    className={`px-3 py-1 text-xs border ${form.category_ids.includes(c.id) ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]" : "border-[var(--border)] hover:border-[var(--text-2)]"}`}
                    style={{ textTransform: "none", letterSpacing: "0" }}
                    data-testid={`vault-cat-${c.id}`}
                  >
                    {localizedName(c.name)}
                  </button>
                ))}
                {!cats.length && <div className="text-xs text-[var(--text-muted)]">Kateqoriya yoxdur</div>}
              </div>
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Format"><input value={form.format} onChange={(e) => setField("format", e.target.value)} className="input" /></Field>
              <Field label="RPM"><input value={form.rpm} onChange={(e) => setField("rpm", e.target.value)} className="input" /></Field>
              <Field label="Ağırlıq"><input value={form.weight} onChange={(e) => setField("weight", e.target.value)} className="input" /></Field>
            </div>

            {/* Spotify Album Link */}
            <div className="border border-[#1DB954]/30 bg-[#1DB954]/5 p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Music2 size={14} className="text-[#1DB954]" />
                  <div className="text-sm font-medium">Spotify Albom Linki</div>
                  {aiBusy && !form.spotify_album_url && <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> AI axtarır…</span>}
                  {spotifyStatus === "resolving" && <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Link təsdiqlənir…</span>}
                  {spotifyStatus === "verified" && !aiBusy && <span className="text-[10px] text-[#1DB954] inline-flex items-center gap-1"><Check size={10} /> Təsdiqləndi</span>}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://open.spotify.com/search/${encodeURIComponent(`${form.artist_name || ""} ${form.title || ""}`.trim())}`}
                    target="_blank"
                    rel="noreferrer"
                    className="px-2.5 py-1 text-xs bg-black/60 text-[#1DB954] border border-[#1DB954]/40 hover:bg-black flex items-center gap-1"
                    data-testid="vault-spotify-search-btn"
                    title="Spotify-də tap → 'Share' → 'Copy link' → linki bura yapışdır"
                  >
                    <Search size={11} /> Spotify-də tap
                  </a>
                  <button
                    type="button"
                    onClick={() => setSpotifyPickerOpen(true)}
                    className="px-2.5 py-1 text-xs bg-[#1DB954] text-white hover:bg-[#1aa34a] flex items-center gap-1"
                    data-testid="vault-spotify-picker-btn"
                  >
                    <Search size={11} /> Canlı Axtar
                  </button>
                </div>
              </div>

              {/* AI could not verify hint */}
              {spotifyStatus === "unverified" && !form.spotify_album_url && (
                <div className="text-[11px] text-[var(--text-muted)] mb-2 leading-relaxed border-l-2 border-[#1DB954]/50 pl-2">
                  <div className="mb-1">AI dəqiq Spotify albom linkini tapa bilmədi. İki variant:</div>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li><b>Dərhal həll:</b> yuxarıdakı <b>Spotify-də tap</b> düyməsinə basın → albomu tapın → <b>Share → Copy link</b> → aşağıdakı xanaya yapışdırın.</li>
                    <li><b>Daimi həll:</b> <a href="/admin/vault#settings" className="text-[#1DB954] underline">Vault → API Açarları</a> bölməsində <b>Spotify Client ID</b> və <b>Secret</b> əlavə edin — bundan sonra AI 100% dəqiq tapacaq (pulsuz alınır: developer.spotify.com/dashboard).</li>
                  </ol>
                </div>
              )}

              <input
                value={form.spotify_album_url || ""}
                onChange={(e) => setField("spotify_album_url", e.target.value)}
                onBlur={async (e) => {
                  const val = (e.target.value || "").trim();
                  if (!val) { setSpotifyStatus("idle"); return; }
                  // Skip if it's a plain search URL — we don't want to store those
                  if (/open\.spotify\.com\/search\//i.test(val)) {
                    setSpotifyResolveErr("Bu axtarış URL-idir, albom URL-i deyil. Albomu açıb Share > Copy link ilə linki götürün.");
                    setSpotifyStatus("unverified");
                    return;
                  }
                  // Quick client-side extraction first — handles majority of paste cases instantly
                  const localM = val.match(/[/]album[/]([A-Za-z0-9]{22})/) || val.match(/spotify:album:([A-Za-z0-9]{22})/) || val.match(/^([A-Za-z0-9]{22})$/);
                  if (localM) {
                    const canonical = `https://open.spotify.com/album/${localM[1]}`;
                    if (canonical !== val) setField("spotify_album_url", canonical);
                    setSpotifyStatus("verified");
                    setSpotifyResolveErr("");
                    return;
                  }
                  // If it looks like a short link or non-standard URL — ask backend to resolve
                  setSpotifyStatus("resolving");
                  setSpotifyResolveErr("");
                  try {
                    const { resolveSpotifyUrl } = await import("../../services/discogs");
                    const res = await resolveSpotifyUrl(val);
                    if (res.album_id) {
                      setField("spotify_album_url", res.canonical_url);
                      setSpotifyStatus(res.verified ? "verified" : "unverified");
                    } else {
                      setSpotifyStatus("unverified");
                      setSpotifyResolveErr("Bu linkdə albom ID-si tapılmadı. Zəhmət olmasa Spotify-də albomu tapıb Share > Copy link ilə linki götürün.");
                    }
                  } catch (ex) {
                    setSpotifyStatus("unverified");
                    setSpotifyResolveErr(`Link yoxlanılmadı: ${ex.message || ex}`);
                  }
                }}
                placeholder="https://open.spotify.com/album/…?si=…"
                className="input mb-2"
                data-testid="vault-f-spotify"
              />

              {spotifyResolveErr && (
                <div className="text-[11px] text-[var(--accent)] mb-2">{spotifyResolveErr}</div>
              )}

              {form.spotify_album_url && !/\/search\//i.test(form.spotify_album_url) && (
                <a href={form.spotify_album_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#1DB954] inline-flex items-center gap-1">
                  Spotify-də aç <ExternalLink size={10} />
                </a>
              )}
              {/* Live Spotify embed preview */}
              {(() => {
                const s = String(form.spotify_album_url || "");
                // Robust: match /album/{id} even in intl-XX paths, ignore ?si= query
                const m = s.match(/[/]album[/]([A-Za-z0-9]{22})/);
                if (!m) return null;
                return (
                  <div className="mt-3">
                    <iframe
                      title="spotify-preview"
                      src={`https://open.spotify.com/embed/album/${m[1]}?theme=0`}
                      width="100%" height="152" frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      style={{ border: 0, borderRadius: 12 }}
                    />
                  </div>
                );
              })()}
            </div>

            {/* Spotify Album Picker modal */}
            <SpotifyAlbumPicker
              open={spotifyPickerOpen}
              onClose={() => setSpotifyPickerOpen(false)}
              onPick={(album) => {
                setField("spotify_album_url", album.url);
                setSpotifyStatus("verified");
                setSpotifyResolveErr("");
              }}
              initialQuery={`${form.artist_name || ""} - ${form.title || ""}`}
              initialBarcode={form.barcode || ""}
            />

            {/* Multi-language descriptions */}
            <div className="border border-[var(--border)] bg-[var(--surface)]/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Languages size={14} className="text-[var(--accent)]" />
                  <div className="text-sm font-medium">Təsvir (AZ / EN / RU)</div>
                  {aiBusy && !form.description_az && (
                    <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
                      <Loader2 size={10} className="animate-spin" /> AI yazır…
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 mb-2">
                {["az", "en", "ru"].map((lg) => (
                  <button
                    key={lg}
                    type="button"
                    onClick={() => setDescTab(lg)}
                    className={`px-3 py-1 text-xs border-b-2 ${descTab === lg ? "border-[var(--accent)] text-[var(--accent)]" : "border-transparent text-[var(--text-muted)]"}`}
                    data-testid={`vault-desc-tab-${lg}`}
                  >
                    {lg.toUpperCase()}
                  </button>
                ))}
              </div>
              {descTab === "az" && (
                <textarea value={form.description_az} onChange={(e) => setField("description_az", e.target.value)} className="input min-h-[120px]" rows={5} placeholder="Azərbaycanca təsvir" />
              )}
              {descTab === "en" && (
                <textarea value={form.description_en} onChange={(e) => setField("description_en", e.target.value)} className="input min-h-[120px]" rows={5} placeholder="English description" />
              )}
              {descTab === "ru" && (
                <textarea value={form.description_ru} onChange={(e) => setField("description_ru", e.target.value)} className="input min-h-[120px]" rows={5} placeholder="Описание на русском" />
              )}
              <div className="text-[10px] text-[var(--text-muted)] mt-1">
                {(form["description_" + descTab] || "").length} simvol
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.new_arrival} onChange={(e) => setField("new_arrival", e.target.checked)} /> Yeni gələn</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e) => setField("featured", e.target.checked)} /> Ön planda</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_used} onChange={(e) => setField("is_used", e.target.checked)} /> İkinci əl</label>
            </div>

            <div className="flex gap-3 pt-3 border-t border-[var(--border)]">
              <button onClick={() => setStep(STEPS.DETAIL)} className="px-4 py-2 border border-[var(--border)] text-sm hover:border-[var(--text-2)]">Geri</button>
              <button onClick={save} disabled={busy} className="btn-primary flex-1 justify-center" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="vault-save">
                {busy ? (
                  <><Loader2 size={14} className="animate-spin" /> {uploadProgress.total > 0 ? `Şəkil ${uploadProgress.current}/${uploadProgress.total} yüklənir…` : "Yadda saxlanır…"}</>
                ) : (
                  <><Package size={14} /> Məhsulu əlavə et ({form.image_uris?.length || 1} şəkil)</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Done */}
      {step === STEPS.DONE && (
        <div className="border border-green-500/30 bg-green-500/5 p-6 text-center space-y-3" data-testid="vault-done">
          <div className="w-12 h-12 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center mx-auto">
            <Check size={24} />
          </div>
          <div className="font-display text-2xl">Məhsul uğurla əlavə edildi</div>
          <div className="text-sm text-[var(--text-muted)]">ID: {savedId}</div>
          <button onClick={reset} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="vault-add-another">
            <Search size={14} /> Yeni barkod skan et
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium block mb-1">
        {label}{required && <span className="text-[var(--accent)]"> *</span>}
      </span>
      {children}
    </label>
  );
}
